import { Button } from "@legal-500/legal500-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useForm,
  Controller,
  type UseFormReturn,
  type FieldValues,
  type Control,
  type FieldErrors,
} from "react-hook-form";
import { useEffect, useImperativeHandle, forwardRef } from "react";
import type { z } from "zod";
import type { FormFieldConfig } from "../../../types/uiTypes";
import {
  TextField,
  NumberField,
  SelectField,
  DateField,
  CheckboxField,
  CheckboxGroupField,
  TextAreaField,
  RadioField,
} from "../fields";
import { SearchableSelect } from "../SearchableSelect/SearchableSelect";
import { SearchableMultiSelect } from "../SearchableSelect/SearchableMultiSelect";
import { AsyncSearchableSelect } from "../AsyncSearchableSelect";
import styles from "./GenericForm.module.css";

type GenericFormProps = {
  fields: Array<
    FormFieldConfig & {
      isMultiCheckbox?: boolean;
      checkboxOptions?: Array<{ value: string; label: string }>;
    }
  >;
  validationSchema: z.ZodTypeAny;
  defaultValues?: FieldValues;
  onSave: (
    data: FieldValues,
    setError?: (field: string, error: { type: string; message: string }) => void,
  ) => Promise<void> | void;
  onCancel: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  submitLabel?: string;
  cancelLabel?: string;
  metadataConfig?: (
    control: Control<FieldValues>,
    errors: FieldErrors<FieldValues>,
  ) => React.ReactNode;
};

export type GenericFormHandle = {
  form: UseFormReturn<FieldValues>;
};

export const GenericForm = forwardRef<GenericFormHandle, GenericFormProps>(
  (
    {
      fields,
      validationSchema,
      defaultValues = {},
      onSave,
      onCancel,
      onDirtyChange,
      submitLabel = "Save",
      cancelLabel = "Cancel",
      metadataConfig,
    },
    ref,
  ) => {
    const form = useForm<FieldValues>({
      resolver: zodResolver(validationSchema as z.ZodObject<Record<string, z.ZodTypeAny>>),
      mode: "onBlur",
      defaultValues,
    });

    const {
      control,
      handleSubmit,
      setError,
      watch,
      setValue,
      formState: { errors, isSubmitting, isDirty },
    } = form;

    // Expose form instance to parent via ref
    useImperativeHandle(ref, () => ({
      form,
    }));

    // Notify parent when dirty state changes
    useEffect(() => {
      if (onDirtyChange) {
        onDirtyChange(isDirty);
      }
    }, [isDirty, onDirtyChange]);

    const formValues = watch();

    const onSubmit = async (data: Record<string, unknown>) => {
      try {
        await onSave(data, setError);
      } catch (error) {
        if (error instanceof Error) {
          const apiError = error as Error & { details?: { field?: string } };

          // If backend specifies a field, set error on that field
          if (apiError.details?.field) {
            setError(apiError.details.field, {
              type: "manual",
              message: error.message,
            });
          } else {
            // Generic form-level error
            setError("root", {
              type: "manual",
              message: error.message,
            });
          }
        } else {
          // Generic form-level error
          setError("root", {
            type: "manual",
            message: String(error),
          });
        }
      }
    };

    // Helper function to render a field based on its configuration
    const renderField = (field: (typeof fields)[number]) => {
      // Multi-checkbox field (sites, languages, etc.)
      if (field.isMultiCheckbox && field.checkboxOptions) {
        return (
          <CheckboxGroupField
            key={field.name}
            field={
              field as typeof field & { checkboxOptions: Array<{ value: string; label: string }> }
            }
            control={control}
            errors={errors}
          />
        );
      }

      // Radio field
      if (field.type === "radio" && field.options) {
        return (
          <RadioField
            key={field.name}
            field={
              {
                ...field,
                options: field.options as Array<{ value: string; label: string }>,
                onChange: field.onChange
                  ? (value: string | number) => {
                      const updatedValues = { ...formValues, [field.name]: value };
                      field.onChange?.(value, updatedValues, setValue);
                    }
                  : undefined,
              } as FormFieldConfig & {
                options: Array<{ value: string; label: string }>;
                onChange?: (value: string | number) => void;
              }
            }
            control={control}
            errors={errors}
          />
        );
      }

      // SearchableSelect field
      if (field.type === "searchableSelect" && field.options) {
        const isMultiple = field.multiple ?? false;
        const maxSelections = field.maxSelections;

        // Resolve options if they're a function
        const resolvedOptions = typeof field.options === "function" ? [] : field.options;

        return (
          <Controller
            key={field.name}
            name={field.name}
            control={control}
            render={({ field: controllerField, fieldState }) =>
              isMultiple ? (
                <SearchableMultiSelect
                  name={field.name}
                  label={field.label}
                  required={field.required ?? false}
                  value={(controllerField.value as number[]) ?? []}
                  options={resolvedOptions}
                  onChange={value => {
                    controllerField.onChange(value);
                    // Get updated values after the change
                    const updatedValues = { ...formValues, [field.name]: value };
                    field.onChange?.(value, updatedValues, setValue);
                  }}
                  disabled={field.disabled ?? false}
                  placeholder={field.placeholder}
                  maxSelections={maxSelections}
                  helperText={field.helperText}
                  error={fieldState.error?.message}
                />
              ) : (
                <SearchableSelect
                  name={field.name}
                  label={field.label}
                  required={field.required ?? false}
                  value={(controllerField.value as string | number | null) ?? null}
                  options={resolvedOptions}
                  onChange={value => {
                    controllerField.onChange(value);
                    // Get updated values after the change
                    const updatedValues = { ...formValues, [field.name]: value };
                    field.onChange?.(value, updatedValues, setValue);
                  }}
                  disabled={field.disabled ?? false}
                  placeholder={field.placeholder}
                  helperText={field.helperText}
                  error={fieldState.error?.message}
                  preserveOptionOrder={field.preserveOptionOrder}
                />
              )
            }
          />
        );
      }

      // AsyncSearchableSelect field
      if (field.type === "asyncSearchableSelect" && field.onSearch) {
        return (
          <Controller
            key={field.name}
            name={field.name}
            control={control}
            render={({ field: controllerField, fieldState }) => (
              <AsyncSearchableSelect
                name={field.name}
                label={field.label}
                required={field.required ?? false}
                value={(controllerField.value as string | number | null) ?? null}
                onSearch={field.onSearch!}
                onChange={value => {
                  controllerField.onChange(value);
                  const updatedValues = { ...formValues, [field.name]: value };
                  field.onChange?.(value, updatedValues, setValue);
                }}
                disabled={field.disabled ?? false}
                placeholder={field.placeholder}
                helperText={field.helperText}
                error={fieldState.error?.message}
                debounceMs={field.debounceMs}
                minSearchLength={field.minSearchLength}
                initialOption={field.initialOption}
              />
            )}
          />
        );
      }

      // Select field
      if (field.type === "select" && field.options) {
        return (
          <SelectField
            key={field.name}
            field={
              {
                ...field,
                options: field.options as Array<{ value: string | number | null; label: string }>,
                onChange: field.onChange
                  ? (value: string | number) => {
                      const updatedValues = { ...formValues, [field.name]: value };
                      field.onChange?.(value, updatedValues, setValue);
                    }
                  : undefined,
              } as FormFieldConfig & {
                options: Array<{ value: string | number | null; label: string }>;
                onChange?: (value: string | number) => void;
              }
            }
            control={control}
            errors={errors}
          />
        );
      }

      // Number field
      if (field.type === "number") {
        return <NumberField key={field.name} field={field} control={control} errors={errors} />;
      }

      // Date field
      if (field.type === "date") {
        return <DateField key={field.name} field={field} control={control} errors={errors} />;
      }

      // Checkbox field (single)
      if (field.type === "checkbox") {
        return (
          <CheckboxField
            key={field.name}
            field={
              {
                ...field,
                onChange: undefined,
              } as FormFieldConfig & {
                onChange?: (value: string | number) => void;
              }
            }
            control={control}
            errors={errors}
          />
        );
      }

      // Textarea field
      if (field.type === "textarea") {
        return <TextAreaField key={field.name} field={field} control={control} errors={errors} />;
      }

      // Text field (default)
      return <TextField key={field.name} field={field} control={control} errors={errors} />;
    };

    const visibleFields = fields.filter(f => {
      if (typeof f.hidden === "function") {
        return !f.hidden(formValues);
      }
      return !f.hidden;
    });

    // Always split fields for two-column layout
    const midpoint = Math.ceil(visibleFields.length / 2);
    const leftFields = visibleFields.slice(0, midpoint);
    const rightFields = visibleFields.slice(midpoint);

    return (
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        {errors.root && (
          <div className={styles.formError} role="alert">
            {errors.root.message}
          </div>
        )}

        <section className={styles.formSection}>
          <div className={styles.twoColumnLayout}>
            <div className={styles.formColumn}>{leftFields.map(field => renderField(field))}</div>

            <div className={styles.formColumn}>{rightFields.map(field => renderField(field))}</div>
          </div>
        </section>

        {metadataConfig && (
          <div className={styles.metadataSection}>{metadataConfig(control, errors)}</div>
        )}

        <div className={styles.formActions}>
          <Button type="button" onClick={onCancel} size="small" variant="outlined">
            {cancelLabel}
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            size="small"
            variant="contained"
            color="primary"
            aria-label={submitLabel}
          >
            {isSubmitting ? "Saving..." : submitLabel}
          </Button>
        </div>
      </form>
    );
  },
);

GenericForm.displayName = "GenericForm";
